/* eslint-disable class-methods-use-this, no-unused-vars */

'use strict';

define('vb/private/helpers/runtimeEnvironment',[
  'vb/private/configLoader',
  'vb/private/log',
  'vb/private/utils',
  'vb/private/services/servicesLoader',
  'vb/private/services/swaggerUtils',
  'vb/private/services/trapData',
], (ConfigLoader, Log, Utils, ServicesLoader,
  SwaggerUtils, TrapData) => {
  // the amount of time in milliseconds to wait for the user session to refresh from the hidden iframe
  const REFRESH_AUTH_TOKEN_TIMEOUT = 3000;

  const badStringUrlSegmentRegex = /\/(undefined|null)\//gi;

  const logger = Log.getLogger('/vb/private/helpers/runtimeEnvironment');

  /**
   * A facade for how we get application artifacts, this class is used by the runtime to perform
   * tasks that are environment specific such as loading files and logging. You can provide a
   * subclass implementation by supplying the following mapping in your requirejs.config
   * before vb/bootstrap is called to load the runtime:
   *
   * map: {
   *  '*': {
   *    vbRuntimeEnvironmentClass: 'DtRtEnvironment'
   *  }
   * }
   *
   * TODO: The contract for this class will be evolving so it's subject to change in the future.
   */
  class RuntimeEnvironment {
    /**
     * Returns the extension manifest of the extension currently being edited in DT.
     *
     * The extension manifest includes details such as:
     * - id: A unique identifier for the extension.
     * - name: The name of the extension.
     * - version: The version of the extension.
     * - files: An array of file paths that belong to the extension.
     * - url: An object containing the base URL for the extension.
     *
     * Example of an extension manifest:
     * {
     *   id: 'extA',
     *   name: 'My Extension A',
     *   version: '1.2.3',
     *   files: [
     *     'base/dynamicLayouts/pet/findByStatus/layout-x.json',
     *     'base/pages/shell-page-x.json',
     *     'base/pages/shell-page-x.js',
     *   ],
     *   url: {
     *     baseUrl: 'extA/sources/ui/extension1/',
     *   },
     * }
     *
     * @returns {Promise<?Object>} a promise that resolves to null or an object representing an extension manifest as
     * defined above.
     */
    getExtensionManifest() {
      return Promise.resolve(null);
    }

    /**
     * Returns a promise that resolves to the type information of the security provider's user info.
     * The type information defines the structure and properties of the user info provided by the security provider.
     *
     * @returns {Promise<*>} A promise that resolves to the user info type. If the security provider is not available,
     * the promise resolves to null or an appropriate value indicating the absence of user info type.
     */
    getUserInfoType() {
      return ConfigLoader.loadSecurityProvider()
        .then((securityProvider) => securityProvider && securityProvider.constructor.getUserInfoType());
    }

    /**
     * Disables the loading of application UIs from the registry.
     * This method is typically used in certain preview modes where loading App UIs is not necessary.
     *
     * @returns {Promise<boolean>} A promise that resolves to `true` to indicate that loading of App UIs should be
     * disabled.
     */
    disableAppUis() {
      return Promise.resolve(false);
    }

    /**
     * Returns the application descriptor as a JSON object.
     *
     * This method fetches the JSON object that describes a specific application flow based on the provided flow
     * locator.
     * The descriptor is typically defined in a JSON file that follows the naming
     * convention of `-flow.json` appended to the flow locator.
     *
     * @param {String} flowLocator - The location of the flow, typically represented as a string, e.g., 'flow1' or
     * 'flow1/subflow1'.
     * @returns {Promise<Object>} A promise that resolves to the application descriptor JSON object.
     * If the resource is successfully obtained, the promise resolves with the application descriptor;
     * otherwise, it may reject with an error.
     */
    getApplicationDescriptor(flowLocator) {
      return Utils.loadAndParse(`${flowLocator}-flow.json`);
    }

    /**
     * Returns the application functions.
     *
     * This method fetches the application functions associated with a specific flow.
     * The functions are typically defined in a script file that follows the naming
     * convention of `-flow.js` appended to the flow locator.
     *
     * @param {String} flowLocator - The location of the flow, e.g., `flow1` or `flow1/subflow1`.
     * @returns {Promise<Object>} A promise that resolves to the application functions.
     * If the resource is successfully obtained, the promise resolves with the application functions;
     * otherwise, it may reject with an error.
     */
    getApplicationFunctions(flowLocator) {
      return Utils.getResource(`${flowLocator}-flow`);
    }

    /**
     * Returns the flow descriptor as a JSON object.
     *
     * The flow descriptor provides detailed information about the specified flow,
     * allowing the runtime environment to manage and utilize the flow appropriately.
     *
     * @param {String} flowLocator - The location of the flow, e.g., 'flow1' or 'flow1/subflow1'.
     * @returns {Promise<Object>} A promise that resolves to the flow descriptor JSON object.
     */
    getFlowDescriptor(flowLocator) {
      return Utils.loadAndParse(`${flowLocator}-flow.json`);
    }

    /**
     * Returns the flow functions.
     *
     * This method fetches the functions associated with a specific flow.
     * The functions are typically defined in a script file that follows the naming
     * convention of `-flow.js` appended to the flow locator.
     *
     * @param {String} flowLocator - The location of the flow, e.g., `flow1` or `flow1/flow2`.
     * @returns {Promise<Object>} A promise that resolves to the flow functions.
     * If the resource is successfully obtained, the promise resolves with the flow functions;
     * otherwise, it may reject with an error.
     */
    getFlowFunctions(flowLocator) {
      return Utils.getResource(`${flowLocator}-flow`);
    }

    /**
     * Returns the flow rules descriptor as a JSON object.
     *
     * This method fetches the flow rules descriptor associated with a specific flow.
     * The flow rules descriptor is typically defined in a JSON file that follows the naming
     * convention of `-flow-rules.json` appended to the flow rules locator.
     *
     * @param {String} flowRulesLocator - The location of the flow rules descriptor, e.g., `flow1` or `flow1/subflow1`.
     * @returns {Promise<Object>} A promise that resolves to the flow rules descriptor JSON object.
     * If the resource is obtained successfully, the promise resolves with the flow rules descriptor;
     * otherwise, it may reject with an error.
     */
    getFlowRulesDescriptor(flowRulesLocator) {
      return Utils.loadAndParse(`${flowRulesLocator}-flow-rules.json`);
    }

    /**
     * Returns the flow extension descriptor for a specified extension.
     *
     * This method fetches the JSON object that describes a specific flow extension.
     * The descriptor is typically defined in a JSON file that follows the naming
     * convention of `-flow-x.json` appended to the extension locator.
     *
     * @param {String} extensionLocator - The location of the extension, which could be in the format of a simple string
     * (e.g., 'extension1') or a path (e.g., 'flow/extension1').
     * @returns {Promise<Object>} - A promise that resolves to the flow extension descriptor JSON object.
     * If the resource is successfully obtained, the promise resolves with the flow extension descriptor;
     * otherwise, it may reject with an error.
     */
    getFlowExtensionDescriptor(extensionLocator) {
      return Utils.loadAndParse(`${extensionLocator}-flow-x.json`);
    }

    /**
     * Returns the flow extension functions.
     *
     * This method fetches the functions associated with a specific flow extension.
     * The functions are typically defined in a script file that follows the naming
     * convention of `-flow-x.js` appended to the extension locator.
     *
     * @param {String} extensionLocator - The location of the flow extension, e.g., `flow1` or `flow1/flow2`.
     * @returns {Promise<Object>} A promise that resolves to the flow extension functions.
     * If the resource is successfully obtained, the promise resolves with the flow extension functions;
     * otherwise, it may reject with an error.
     */
    getFlowExtensionFunctions(extensionLocator) {
      return Utils.getResource(`${extensionLocator}-flow-x`);
    }

    /**
     * Returns the flow extension's rules descriptor as a JSON object.
     *
     * This method fetches the flow extension rules descriptor associated with a specific flow extension.
     * The rules descriptor is typically defined in a JSON file that follows the naming
     * convention of `-flow-rules-x.json` appended to the flow extension rules locator.
     *
     * @param {String} flowExtRulesLocator - The location of the flow extension rules descriptor, e.g., `extension1` or
     * `flow1/extension1`.
     * @returns {Promise<Object>} A promise that resolves to the flow extension rules descriptor JSON object.
     * If the resource is successfully obtained, the promise resolves with the flow extension rules descriptor;
     * otherwise, it may reject with an error.
     */
    getFlowExtensionRulesDescriptor(flowExtRulesLocator) {
      return Utils.loadAndParse(`${flowExtRulesLocator}-flow-rules-x.json`);
    }

    /**
     * Returns the App UI descriptor as a JSON object.
     *
     * This method fetches the JSON object that describes the App UI.
     * The descriptor is typically defined in a JSON file, where the naming
     * convention follows the '<packageLocator>.json' format.
     *
     * @param {String} packageLocator - The location of the App UI, e.g., 'appUi1' or 'appUi1/uiComponent'.
     * @returns {Promise<Object>} - A promise that resolves to the App UI descriptor JSON object.
     * If the resource is successfully obtained, the promise resolves with the App UI descriptor;
     * otherwise, it may reject with an error.
     */
    getAppUiDescriptor(packageLocator) {
      return Utils.loadAndParse(`${packageLocator}.json`);
    }

    /**
     * Returns the App UI functions.
     *
     * This method fetches the JavaScript functions associated with a specific App UI package.
     * The functions are typically defined in a script file whose location is given by the `packageLocator`.
     *
     * @param {String} packageLocator - The location of the App UI, e.g., `appUiPackage` or `appUiPackage/component`.
     * @returns {Promise<Object>} A promise that resolves to the App UI functions.
     * If the resource is successfully obtained, the promise resolves with the App UI functions;
     * otherwise, it may reject with an error.
     */
    getAppUiFunctions(packageLocator) {
      return Utils.getResource(packageLocator);
    }

    /**
     * Returns the App UI extension descriptor as a JSON object.
     *
     * This method fetches the JSON object that describes the App UI extension.
     * The descriptor is typically defined in a JSON file that follows the naming
     * convention of `<packageLocator>-x.json`.
     *
     * @param {string} packageLocator - The location of the App UI package. For example, 'appUi1' or
     * 'appUi1/uiComponent'.
     * @returns {Promise<Object>} - A promise that resolves to the App UI extension descriptor JSON object.
     * If the resource is successfully obtained, the promise resolves with the App UI extension descriptor;
     * otherwise, it may reject with an error.
     */
    getAppUiExtensionDescriptor(packageLocator) {
      return Utils.loadAndParse(`${packageLocator}-x.json`);
    }

    /**
     * Returns the App UI extension functions.
     *
     * This method fetches the JavaScript functions associated with a specific App UI extension.
     * The functions are typically defined in a script file that follows the naming
     * convention of `-x.js` appended to the extension locator.
     *
     * @param {String} extensionLocator - The location of the App UI package extension, e.g., `appUiPackage` or
     * `appUiPackage/extension`.
     * @returns {Promise<Object>} A promise that resolves to the App UI extension functions.
     * If the resource is successfully obtained, the promise resolves with the App UI extension functions;
     * otherwise, it may reject with an error.
     */
    getAppUiExtensionFunctions(extensionLocator) {
      return Utils.getResource(`${extensionLocator}-x`);
    }

    /**
     * Returns the page descriptor as a JSON object.
     *
     * This method fetches the JSON object that describes a specific page based on the provided page locator.
     * The descriptor is typically defined in a JSON file that follows the naming
     * convention of `-page.json` to the page locator.
     *
     * @param {String} pageLocator - The location of the page, e.g., `page1` or `flow1/page1`.
     * @returns {Promise<Object>} - A promise that resolves to the page descriptor JSON object.
     * If the resource is successfully obtained, the promise resolves with the page descriptor;
     * otherwise, it may reject with an error.
     */
    getPageDescriptor(pageLocator) {
      return Utils.loadAndParse(`${pageLocator}-page.json`);
    }

    /**
     * Returns the HTML template of a specified page.
     *
     * This method fetches the HTML template associated with a specific page based on the provided page locator.
     * The template is typically defined in a file that follows the naming
     * convention of `-page.html` appended to the page locator.
     *
     * @param {String} pageLocator - The location of the page, e.g., `page1` or `flow1/page1`.
     * @returns {Promise<string>} A promise that resolves to the contents of the page template as a string.
     */
    getPageTemplate(pageLocator) {
      return Utils.getResource(`text!${pageLocator}-page.html`);
    }

    /**
     * Returns the page functions.
     *
     * This method fetches the JavaScript functions associated with a specific page.
     * The functions are typically defined in a script file that follows the naming
     * convention of `-page.js` appended to the page locator.
     *
     * @param {String} pageLocator - The location of the page, e.g., `page1` or `flow1/page1`.
     * @returns {Promise<Object>} A promise that resolves to the page functions.
     * If the resource is successfully obtained, the promise resolves with the page functions;
     * otherwise, it may reject with an error.
     */
    getPageFunctions(pageLocator) {
      return Utils.getResource(`${pageLocator}-page`);
    }

    /**
     * Returns the page rules descriptor as a JSON object.
     *
     * This method fetches the page rules descriptor associated with a specific page.
     * The page rules descriptor is typically defined in a JSON file that follows the naming
     * convention of `-page-rules.json` appended to the page rules locator.
     *
     * @param {String} pageRulesLocator - The location of the page rules, e.g., `page1-rules` or `flow1/page1-rules`.
     * @returns {Promise<Object>} A promise that resolves to the page rules descriptor JSON object.
     * If the resource is obtained successfully, the promise resolves with the page rules descriptor;
     * otherwise, it may reject with an error.
     */
    getPageRulesDescriptor(pageRulesLocator) {
      return Utils.loadAndParse(`${pageRulesLocator}-page-rules.json`);
    }

    /**
     * Returns the extension descriptor as a page JSON object.
     *
     * This method fetches the JSON object that describes a specific page extension.
     * The descriptor is typically defined in a JSON file that follows the naming
     * convention of `-page-x.json` appended to the extension locator.
     *
     * @param {String} extensionLocator - The location of the extension, e.g., `page1` or `flow1/page1`.
     * @returns {Promise<Object>} A promise that resolves to the page extension descriptor JSON object.
     * If the resource is obtained successfully, the promise resolves with the page extension descriptor;
     * otherwise, it may reject with an error.
     */
    getPageExtensionDescriptor(extensionLocator) {
      return Utils.loadAndParse(`${extensionLocator}-page-x.json`);
    }

    /**
     * Returns the page extension functions.
     *
     * This method fetches the JavaScript functions associated with a specific page extension.
     * The functions are typically defined in a script file that follows the naming
     * convention of `-page-x.js` appended to the extension locator.
     *
     * @param {String} extensionLocator - The location of the page extension, e.g., `page1` or `flow1/page1`.
     * @returns {Promise<Object>} A promise that resolves to the page extension functions.
     * If the resource is successfully obtained, the promise resolves with the page extension functions;
     * otherwise, it may reject with an error.
     */
    getPageExtensionFunctions(extensionLocator) {
      return Utils.getResource(`${extensionLocator}-page-x`);
    }

    /**
     * Returns the page extension rules descriptor as a JSON object.
     *
     * This method fetches the page extension rules descriptor associated with a specific page extension.
     * The rules descriptor is typically defined in a JSON file that follows the naming
     * convention of `-page-rules-x.json` appended to the page extension rules locator.
     *
     * @param {String} pageExtRulesLocator - The location of the page extension rules, e.g., `page1-x-rules` or
     * `flow1/page1-x-rules`.
     * @returns {Promise<Object>} A promise that resolves to the page extension rules descriptor JSON object.
     * If the resource is obtained successfully, the promise resolves with the page extension rules descriptor;
     * otherwise, it may reject with an error.
     */
    getPageExtensionRulesDescriptor(pageExtRulesLocator) {
      return Utils.loadAndParse(`${pageExtRulesLocator}-page-rules-x.json`);
    }

    /**
     * Returns the fragment descriptor as a JSON object.
     *
     * This method fetches the JSON object that describes a specific fragment based on the provided fragment locator.
     * The descriptor is typically defined in a JSON file that follows the naming
     * convention of `-fragment.json` appended to the fragment locator.
     *
     * @param {String} fragmentLocator - The location of the fragment, e.g., 'fragments/frag1/frag1'.
     * @returns {Promise<Object>} A promise that resolves to the fragment descriptor as a JSON object.
     * If the resource is successfully obtained, the promise resolves with the fragment descriptor;
     * otherwise, it may reject with an error.
     */
    getFragmentDescriptor(fragmentLocator) {
      return Utils.loadAndParse(`${fragmentLocator}-fragment.json`);
    }

    /**
     * Returns the fragment template.
     *
     * This method fetches the HTML template associated with a specific fragment.
     * The template is typically defined in a file that follows the naming
     * convention of `-fragment.html` appended to the fragment locator.
     *
     * @param {String} fragmentLocator - The location of the fragment, e.g., `fragments/frag1/frag1`.
     * @returns {Promise<string>} A promise that resolves to the fragment template as a string.
     * If the resource is successfully obtained, the promise resolves with the fragment template;
     * otherwise, it may reject with an error.
     */
    getFragmentTemplate(fragmentLocator) {
      return Utils.getResource(`text!${fragmentLocator}-fragment.html`);
    }

    /**
     * Returns the functions associated with a specific fragment.
     *
     * This method fetches the JavaScript functions defined in a script file
     * that follows the naming convention `-fragment.js` appended to the fragment locator.
     *
     * @param {String} fragmentLocator - The location of the fragment, e.g., `fragments/frag1/frag1`.
     * @returns {Promise<Object>} A promise that resolves to the fragment functions.
     * If the resource is successfully obtained, the promise resolves with the fragment functions;
     * otherwise, it may reject with an error.
     */
    getFragmentFunctions(fragmentLocator) {
      return Utils.getResource(`${fragmentLocator}-fragment`);
    }

    /**
     * Returns the fragment rules descriptor as a JSON object.
     *
     * This method fetches the fragment rules descriptor associated with a specific fragment.
     * The fragment rules descriptor is typically defined in a JSON file that follows the naming
     * convention of `-fragment-rules.json` appended to the fragment rules locator.
     *
     * @param {String} fragmentRulesLocator - The location of the fragment rules descriptor, e.g.,
     * `fragments/frag1/frag1`.
     * @returns {Promise<Object>} - A promise that resolves to the fragment rules descriptor JSON object.
     * If the resource is successfully obtained, the promise resolves with the fragment rules descriptor;
     * otherwise, it may reject with an error.
     */
    getFragmentRulesDescriptor(fragmentRulesLocator) {
      return Utils.loadAndParse(`${fragmentRulesLocator}-fragment-rules.json`);
    }

    /**
     * Returns the extension descriptor as a fragment JSON object.
     *
     * This method fetches the JSON object that describes a specific fragment extension.
     * The descriptor is typically defined in a JSON file that follows the naming
     * convention of `-fragment-x.json` appended to the extension locator.
     *
     * @param {string} extensionLocator - The location of the extension, e.g., 'fragments/ext1'.
     * @returns {Promise<Object>} - A promise that resolves to the fragment extension descriptor JSON object.
     * If the resource is successfully obtained, the promise resolves with the fragment extension descriptor;
     * otherwise, it may reject with an error.
     */
    getFragmentExtensionDescriptor(extensionLocator) {
      return Utils.loadAndParse(`${extensionLocator}-fragment-x.json`);
    }

    /**
     * Returns the fragment extension functions.
     *
     * This method fetches the JavaScript functions associated with a specific fragment extension.
     * The functions are typically defined in a script file that follows the naming
     * convention of `-fragment-x.js` appended to the fragment locator.
     *
     * @param {String} fragmentLocator - The location of the fragment, e.g., `fragments/frag1/frag1`.
     * @returns {Promise<Object>} A promise that resolves to the fragment extension functions.
     * If the resource is successfully obtained, the promise resolves with the fragment extension functions;
     * otherwise, it may reject with an error.
     */
    getFragmentExtensionFunctions(fragmentLocator) {
      return Utils.getResource(`${fragmentLocator}-fragment-x`);
    }

    /**
     * Returns the fragment extension's rules descriptor as a JSON object.
     *
     * This method fetches the fragment extension rules descriptor for a specific fragment extension.
     * The fragment extension rules descriptor is typically defined in a JSON file that follows the naming
     * convention of `-fragment-rules-x.json` appended to the fragment extension rules locator.
     *
     * @param {String} rulesExtensionLocator - The location of the extension rules, e.g., `extension1` or
     * `fragments/ext1`.
     * @returns {Promise<Object>} A promise that resolves to the fragment extension rules descriptor JSON object.
     * If the resource is successfully obtained, the promise resolves with the fragment extension rules descriptor;
     * otherwise, it may reject with an error.
     */
    getFragmentExtensionRulesDescriptor(rulesExtensionLocator) {
      return Utils.loadAndParse(`${rulesExtensionLocator}-fragment-rules-x.json`);
    }

    /**
     * Loads the object containing the string map for the current locale (as determined by JET).
     * This uses the JET `ojL10N` plugin to load "version 1" strings.
     *
     * @param {string} path - The path to the locale bundle resource.
     * @returns {Promise<Object>} A promise that resolves to an object mapping keys to locale-specific strings.
     *
     * @see RuntimeEnvironment#getV2Strings
     */
    getLocaleBundle(path) {
      return Utils.getResource(`ojL10n!${path}`);
    }

    /**
     * Returns an object containing the string map functions for V2 strings.
     * This method processes the given path to ensure that it is valid for retrieving the V2 string bundle,
     * and then fetches the resource using the sanitized path.
     *
     * The path is sanitized by stripping any invalid segments, making it easier to create valid URLs.
     * The returned object has one property:
     * - functions: the compiled JET string bundle functions.
     *
     * Note: Previously, V2Strings mapped the key functions to keys in a 'wrappers' property of the return value.
     * However, it is now decided that V2 strings should use the V2 EL syntax where all string keys are functions
     * whether they have parameters or not.
     *
     * At runtime, there should always be a valid locale path segment. But at design time, there is a concept of a
     * ‘base bundle’ that serves as the default and is selected by setting the locale value to undefined.
     * This avoids the need for ternary operators in every path expression to create valid URLs, making it easier
     * to strip the invalid segment instead.
     *
     * @param {string} path - The path to the locale bundle resource.
     * @param {Object} [declaration] - The original, unevaluated declaration (currently not used by runtime).
     *
     * @returns {Promise<{functions: Object}>} - A promise that resolves to an object with one property, `functions`,
     *                                          which contains the compiled JET string bundle functions.
     */
    getV2Strings(path, declaration) {
      const sanitized = path.replace(badStringUrlSegmentRegex, '/');
      return Utils.getResource(sanitized)
        .then((functions) => ({
          // the string bundle functions
          functions,
        }));
    }

    /**
     * Generic method to load text files.
     *
     * This method fetches the contents of a text file from the specified path and returns it as a promise.
     *
     * @param {string} path - The path to the text file to be loaded.
     * @returns {Promise<string>} A promise that resolves to the contents of the text file as a string.
     */
    getTextResource(path) {
      return Utils.getTextResource(path);
    }

    /**
     * Generic method to load source modules.
     *
     * This method fetches the resource located at the given path. The resource can be any type of module, such as
     * JavaScript files, JSON data, or other assets. The method returns a Promise that resolves to the loaded
     * resource.
     *
     * @param {String} path - The path to the resource module to be loaded.
     * @returns {Promise<*>} - A promise that resolves to the loaded resource. The type of the resolved value
     *                         depends on the type of resource being loaded. It could resolve to a string, an
     *                         object, or any other type of data depending on the resource.
     */
    getModuleResource(path) {
      return Utils.getResource(path);
    }

    /**
     * Generic method to load text files.
     *
     * This method fetches the contents of a text file from the specified path and returns it as a promise.
     *
     * @param {string} path - The path to the text file to be loaded.
     * @returns {Promise<string>} A promise that resolves to the contents of the text file as a string.
     */
    getExtensionTextResource(path) {
      return Utils.getTextResource(path);
    }

    /**
     * Generic method to load source modules.
     *
     * This method fetches the resource located at the given path. The resource can be any type of module, such as
     * JavaScript files, JSON data, or other assets. The method returns a Promise that resolves to the loaded
     * resource.
     *
     * @param {String} path - The path to the resource module to be loaded.
     * @returns {Promise<*>} - A promise that resolves to the loaded resource. The type of the resolved value
     *                         depends on the type of resource being loaded. It could resolve to a string, an
     *                         object, or any other type of data depending on the resource.
     */
    getExtensionModuleResource(path) {
      return Utils.getResource(path);
    }

    /**
     * Returns an array of paths to modules that the runtime should preload before the application starts.
     *
     * This method returns a promise that resolves to an array of string paths. These paths correspond to the modules
     * that need to be preloaded to ensure the application has all necessary resources available at startup.
     *
     * @returns {Promise<string[]>} A promise that resolves to an array of module paths to preload.
     *                              Each path is a string representing the location of the module.
     */
    getModulesToPreload() {
      return Promise.resolve([]);
    }

    /**
     * This method is called when an error is logged by the logger. The error can either be a string or an instance of
     * Error. It allows various arguments, which can be a combination of strings and Error objects.
     *
     * @param {...(string|Error)} args - The error details to log, which can be a combination of strings and Error
     * objects. Each argument can be:
     *  - {string}: A message describing the error.
     *  - {Error}: An instance of Error containing error details.
     *
     * @example
     * // Logging a simple error message
     * notifyError('An error occurred');
     *
     * @example
     * // Logging an error object
     * const errorObj = new Error('Something went wrong');
     * notifyError(errorObj);
     *
     * @example
     * // Logging a combination of a message and an error object
     * const errorObj = new Error('Failed to load data');
     * notifyError('Error loading data:', errorObj);
     */
    notifyError(...args) {}

    /**
     * Determines whether navigation can proceed from the current page to the destination page.
     *
     * This method serves as a callback that allows the runtime environment to decide if navigating
     * from the current page to the given destination page is permitted. It returns a promise that
     * resolves to a boolean value indicating whether the navigation can proceed.
     *
     * @param {String} currentPath - The path of the current page.
     * @param {String} destinationPath - The path of the destination page.
     * @returns {Promise<boolean>} A promise that resolves to `true` if navigation can proceed,
     *                             otherwise `false`.
     */
    canNavigateToPage(currentPath, destinationPath) {
      return Promise.resolve(true);
    }

    /**
     * Determines whether navigation can proceed from the current page based on the provided options.
     *
     * This method serves as a callback that allows the runtime environment to determine if navigating
     * from the current page according to the specified options is permitted. It returns a promise that
     * resolves to a boolean value indicating whether the navigation can proceed.
     *
     * @param {String} currentPath - The path of the current page.
     * @param {Object} options - The set of options defining the navigation.
     * @returns {Promise<boolean>} A promise that resolves to `true` if navigation can proceed,
     *                             otherwise `false`.
     */
    canNavigate(currentPath, options) {
      return Promise.resolve(true);
    }

    /**
     * Determines whether navigation can proceed from the current page to the specified URL.
     *
     * This method serves as a callback that allows the runtime environment to decide if navigating
     * from the current page to the given URL is permitted. It returns a promise that resolves to
     * a boolean value indicating whether the navigation can proceed.
     *
     * @param {String} currentPath - The path of the current page.
     * @param {String} url - The destination URL.
     * @returns {Promise<boolean>} A promise that resolves to `true` if navigation can proceed,
     *                             otherwise `false`.
     */
    canNavigateToUrl(currentPath, url) {
      return Promise.resolve(true);
    }

    /**
     * Determines whether navigation can proceed from the current page back to the previous URL
     * in the browser history.
     *
     * This method serves as a callback that allows the runtime environment to decide if navigating
     * back from the current page is permitted. It returns a promise that resolves to a boolean
     * value indicating whether the navigation can proceed.
     *
     * @param {String} currentPath - The path of the current page.
     * @returns {Promise<boolean>} A promise that resolves to `true` if navigation can proceed,
     *                             otherwise `false`.
     */
    canNavigateBack(currentPath) {
      return Promise.resolve(true);
    }

    /**
     * Determines whether the openUrl action can proceed.
     *
     * This method serves as a callback that allows the runtime environment to decide if navigating
     * to a given destination URL is permitted. It returns a promise that resolves to a boolean value
     * indicating whether the URL can be opened.
     *
     * @param {String} currentPath - The path of the current page.
     * @param {String} url - The destination URL to which the navigation is attempted.
     * @param {String} windowName - The name of the window in which the URL should be opened.
     *                             This corresponds to the `windowName` parameter of the `Router.openUrl()` call.
     * @returns {Promise<boolean>} - A promise that resolves to `true` if the `openUrl` action can proceed,
     *                               otherwise `false`.
     */
    canOpenUrl(currentPath, url, windowName) {
      return Promise.resolve(true);
    }

    /**
     * Returns a promise which resolves to an array of user roles that override the logged-in user's roles.
     * If no override is needed, the promise should resolve to undefined.
     *
     * @returns {Promise<string[]|undefined>} A promise that resolves to an array of user role strings or undefined.
     *
     * @example
     * runtimeEnvironment.getUserRolesOverride()
     *   .then((roles) => {
     *     if (roles) {
     *       console.log('Overridden user roles:', roles);
     *     } else {
     *       console.log('No role overrides.');
     *     }
     *   })
     *   .catch((error) => {
     *     console.error('Error fetching user roles override:', error);
     *   });
     */
    getUserRolesOverride() {
      return Promise.resolve(undefined);
    }

    /**
     * Returns a promise of user permissions that override the logged-in user's permissions.
     * If the logged-in user's permissions should not be overridden, the promise should resolve to undefined.
     *
     * @returns {Promise<string[]|undefined>} A promise that resolves to an array of strings representing user
     * permissions or undefined if there are no overrides.
     *
     * @example
     * runtimeEnvironment.getUserPermissionsOverride()
     *   .then((permissions) => {
     *     if (permissions) {
     *       console.log('Overridden user permissions:', permissions);
     *     } else {
     *       console.log('No permission overrides.');
     *     }
     *   })
     *   .catch((error) => {
     *     console.error('Error fetching user permissions override:', error);
     *   });
     */
    getUserPermissionsOverride() {
      return Promise.resolve(undefined);
    }

    /**
     * Saves the provided parameters that can later be retrieved by calling `loadInputParameters`.
     * This method can be overridden by the Designer Toolkit (DT) to pass parameters when navigating between
     * two pages loaded in different iframes.
     *
     * Note: To obtain the parameters as a plain object, call the `plainParams` getter on the `parameters` object.
     *
     * @param {Object} parameters - An object that contains a `plainParams` getter for retrieving the parameters
     *                              as a plain object.
     */
    saveInputParameters(parameters) {}

    /**
     * Loads the parameters saved by the `saveInputParameters` method.
     *
     * This method is typically overridden by the Designer Toolkit (DT) to pass parameters
     * when navigating between two pages that are loaded in different iframes. It allows
     * the runtime to retrieve the parameters that were stored earlier.
     * @return {Object | undefined}
     */
    loadInputParameters() {}

    /**
     * Returns a promise that resolves to a RequireJS configuration object.
     * This configuration object will be set on the service worker.
     * For example:
     * {
     *   paths: {
     *     vbdtsw: 'some url'
     *   }
     * }
     *
     * @returns {Promise<Object>} A promise that resolves to the RequireJS configuration object.
     */
    getServiceWorkerRequireConfig() {
      // Return undefined for a quick way to bail out. Returning null or an empty object would also work.
      // See code in configLoader.js
      return Promise.resolve();
    }

    /**
     * Returns a promise that resolves to an array of plugin information for service workers.
     *
     * Each item in the array can either be a URL string representing the plugin, or an object containing
     * URL and parameters properties. The `params` property will be passed to the constructor of the plugin.
     *
     * Example:
     * [
     *   'vbsw/plugin1',
     *   {
     *     url: 'vbsw/plugin2',
     *     params: {
     *       foo: 'bar'
     *     }
     *   }
     * ]
     *
     * @returns {Promise<Array>} A promise that resolves to an array of plugin info. Each plugin info can be
     * a URL string or an object containing `url` and `params` properties.
     */
    getServiceWorkerPlugins() {
      return Promise.resolve([]);
    }

    /**
     * Handles user session expiry. This method attempts to refresh the session by redirecting a hidden iframe to the
     * login URL. If the session is refreshed successfully, the returned promise resolves to true, allowing the
     * originating request to be retried. If the session refresh fails or times out, the promise resolves to false or
     * rejects with an error message.
     *
     * @param {string} loginUrl - The URL to which the hidden iframe should be redirected for refreshing the session.
     * @returns {Promise<boolean|string>} - A promise that resolves to true if the session is refreshed successfully,
     *                                      false if the session refresh fails, or rejects with an error message.
     */
    sessionExpired(loginUrl) {
      if (!loginUrl) {
        return Promise.reject('Cannot refresh session without a login url.');
      }

      return new Promise((resolve, reject) => {
        // get the hidden iframe for refreshing the auth token
        const refreshTokenIframe = document.getElementById('vb-refresh-access-token-iframe');

        if (refreshTokenIframe) {
          let timeout;

          // listener for the event from the hidden iframe
          const listener = (e) => {
            if (e.origin === window.location.origin && e.data.method === 'vbRefreshAuthToken') {
              // clear the timeout and remove the listener
              clearTimeout(timeout);
              window.removeEventListener('message', listener);

              // return true so we can retry the request
              resolve(true);
            }
          };
          window.addEventListener('message', listener);

          // redirect the hidden iframe to the login url
          // @ts-ignore to fix TS2339: Property 'src' does not exist on type 'HTMLElement'
          refreshTokenIframe.src = loginUrl;

          timeout = setTimeout(() => {
            window.removeEventListener('message', listener);
            reject('Refreshing auth token timed out.');

            // redirect the application to the login url
            window.location.href = loginUrl;
          }, REFRESH_AUTH_TOKEN_TIMEOUT);
        } else {
          // since there's no hidden iframe, simply reject the originating request and redirect the application
          // to the login url
          reject('Redirecting the application to the login url');
          window.location.href = loginUrl;
        }
      });
    }

    /**
     * Returns a promise that resolves to an object containing service swagger extension overrides.
     *
     * The returned object may contain configuration overrides for the service swagger extensions.
     * For example:
     * {
     *   'dt-serviceAuthentication': {
     *     implicitFlow: false,
     *   },
     * }
     *
     * @returns {Promise<Object>} A promise that resolves to an object containing service swagger extension overrides,
     * or null if no overrides are provided.
     *
     * @example
     * runtimeEnvironment.getServiceExtensionOverride()
     *   .then((override) => {
     *     if (override) {
     *       console.log('Service extension override:', override);
     *     } else {
     *       console.log('No service extension overrides.');
     *     }
     *   })
     *   .catch((error) => {
     *     console.error('Error fetching service extension override:', error);
     *   });
     */
    getServiceExtensionOverride() {
      return Promise.resolve(null);
    }

    /**
     * Fetches the service definition from the given path.
     *
     * This method abstracts the process of retrieving the `service.json` file. The path provided
     * must be previously prepended with `Router.baseUrl` if it is a relative path. Optionally,
     * a `requestInit` object can be provided for additional fetch configuration.
     *
     * @param {string} path - The URL or relative path to the service definition file.
     * @param {Object} [requestInit={}] - Optional configuration for the fetch() Request object.
     * @returns {Promise<Object>|Promise<{data: Object, headers: Headers}>} - A promise that resolves to the service
     * definition, or an object containing the data and headers. If the fetch fails, the promise will be rejected.
     * @deprecated Use {@link RuntimeEnvironment#getServiceDefinitionInfo} to get both the service definition and
     * response headers.
     */
    getServiceDefinition(path, requestInit = {}) {
      return ServicesLoader.loadWithTimeout(path, requestInit);
    }

    /**
     * Fetches the service definition from the given path along with the response headers.
     *
     * This method abstracts the process of retrieving the `service.json` file. The path provided
     * must be previously prepended with `Router.baseUrl` if it is a relative path. Optionally,
     * a `requestInit` object can be provided for additional fetch configuration.
     *
     * The returned promise resolves with an object containing the service definition data and response headers.
     * This method also maintains backward compatibility by allowing `getServiceDefinition` to return either
     * just the service definition, or an info object that contains the headers in addition to the openapi doc.
     *
     * @param {string} path - The URL or relative path to the service definition file.
     * @param {Object} [requestInit] - Optional configuration for the fetch() Request object.
     * @returns {Promise<{data: Object, headers: Headers}>} - A promise that resolves to an object containing the
     * service definition (as the `data` property) and the response headers. If the fetch fails, the promise will be
     * rejected.
     */
    getServiceDefinitionInfo(path, requestInit = {}) {
      // For backword compatibility we allow getServiceDefinition() to return either just the service definition,
      // or the info object that contains the headers in addition to the openapi doc
      return this.getServiceDefinition(path, requestInit).then((def) => {
        if (def.data) {
          return def;
        }
        return ({ data: def, headers: new Headers() });
      });
    }

    /**
     * Loads a transform (RequireJS) module source.
     *
     * This method fetches the resource located at the given path. The resource can be any type of module,
     * such as JavaScript files, JSON data, or other assets related to transforms.
     * The method returns a Promise that resolves to the loaded resource.
     *
     * @param {string} path - The path to the transform module to be loaded.
     * @returns {Promise<*>} - A promise that resolves to the loaded resource. The type of the resolved value
     *                         depends on the type of resource being loaded. It could resolve to a string, an
     *                         object, or any other type of data depending on the resource.
     */
    getTransformsSource(path) {
      return Utils.getResource(path);
    }

    /**
     * Loads the `catalog.json` file, which handles the "vb-catalog://" protocol references.
     * The `catalog.json` file typically contains configuration and metadata information for service extensions.
     *
     * @param {string} path - The path to the `catalog.json` file.
     *                        The path can be a relative or absolute URL.
     * @returns {Promise<string>} - A promise that resolves to the JSON string representing the contents
     *                              of the `catalog.json` file.
     *
     * @example
     * runtimeEnvironment.getServiceExtensionCatalog('path/to/catalog.json')
     *   .then((catalogJson) => {
     *     console.log('Catalog JSON:', catalogJson);
     *   })
     *   .catch((error) => {
     *     console.error('Error loading service extension catalog:', error);
     *   });
     */
    getServiceExtensionCatalog(path) {
      return Utils.getResource(`text!${path}`);
    }

    /**
     * Retrieves local server overrides for backends and services.
     * By default, the value is read from vbInitConfig[CATALOG_OVERRIDE].
     *
     * @returns {Promise<string|Object>} A promise that resolves to either a string or an object specifying the
     * catalog overrides.
     *
     * @example
     * runtimeEnvironment.getCatalogOverride()
     *   .then((overrides) => {
     *     console.log('Catalog overrides:', overrides);
     *   })
     *   .catch((error) => {
     *     console.error('Error fetching catalog overrides:', error);
     *   });
     */
    getCatalogOverride() {
      if (!this._catalogOverrides) {
        this._catalogOverrides = Promise.resolve()
          .then(() => (globalThis.vbInitConfig && globalThis.vbInitConfig.CATALOG_OVERRIDE));
      }
      return this._catalogOverrides;
    }

    /**
     * Returns information about server overrides fetched from the RIS TRAP service endpoint.
     *
     * This method attempts to retrieve server override details from the TRAP (Test, Reproduce, Adjust, and Patch)
     * service endpoint. It caches the result to avoid redundant calls. If the request to the TRAP service succeeds,
     * the server details are parsed and returned. If the request fails or an error occurs, a warning is logged and
     * the method resolves to undefined.
     *
     * @returns {Promise<Object|undefined>} A promise that resolves to an object containing the server override details,
     * or undefined if the retrieval fails.
     *
     * @example
     * runtimeEnvironment.getTrapCatalogOverrides()
     *   .then((overrides) => {
     *     if (overrides) {
     *       console.log('Server overrides:', overrides);
     *     } else {
     *       console.log('No server overrides found.');
     *     }
     *   })
     *   .catch((error) => {
     *     console.error('Error fetching server overrides:', error);
     *   });
     */
    getTrapCatalogOverrides() {
      if (!this._trapCatalogOverrides) {
        this._trapCatalogOverrides = Promise.resolve()
          .then(() => TrapData.getTrapData().fetchTrapServiceServers())
          .then((response) => {
            if (response) {
              if (response.ok) {
                return response.text();
              }
              logger.warn('failed to load servers details from TRAP service', response.url, response.status);
            }
            return undefined;
          })
          .then((trapServersText) => trapServersText && SwaggerUtils.parseServiceText(trapServersText))
          .catch((error) => {
            logger.warn('failed to load servers details from TRAP service', error);
            return undefined;
          });
      }
      return this._trapCatalogOverrides;
    }

    /**
     * Gets the configuration object providing information for making calls to the VB TRAP endpoints,
     * which are used for local server overrides.
     *
     * This method returns a cached promise that resolves to the TRAP service overrides configuration. If the
     * configuration has not been previously fetched, it will retrieve the configuration from the `vbInitConfig`
     * global object.
     *
     * @returns {Promise<Object>} A promise that resolves to the configuration object for VB TRAP service overrides.
     *
     * @example
     * runtimeEnvironment.getLocalTrapConfiguration()
     *   .then((trapConfig) => {
     *     if (trapConfig) {
     *       console.log('TRAP service overrides configuration:', trapConfig);
     *     } else {
     *       console.log('No TRAP service overrides configuration found.');
     *     }
     *   })
     *   .catch((error) => {
     *     console.error('Error fetching TRAP service overrides configuration:', error);
     *   });
     */
    getLocalTrapConfiguration() {
      if (!this._localTrapConfig) {
        this._localTrapConfig = Promise.resolve()
          .then(() => (globalThis.vbInitConfig && globalThis.vbInitConfig.TRAP_SERVICE_OVERRIDES));
      }
      return this._localTrapConfig;
    }

    /**
     * Loads a programmatic endpoint plugin (RequireJS) module source.
     *
     * This method fetches the resource located at the given path. The resource can be any type of module,
     * such as JavaScript files, JSON data, or other assets related to endpoint plugins.
     * The method returns a Promise that resolves to the loaded resource.
     *
     * @param {string} path - The path to the endpoint plugin module to be loaded.
     * @returns {Promise<*>} - A promise that resolves to the loaded resource. The type of the resolved value
     *                         depends on the type of resource being loaded. It could resolve to a string, an
     *                         object, or any other type of data depending on the resource.
     */
    getEndpointPlugin(path) {
      return Utils.getResource(path);
    }

    /**
     * Determines whether open tracing should be enabled in the current environment.
     * If open tracing is enabled, the runtime will attempt to initialize a Tracer.
     *
     * @returns {boolean} - A boolean value indicating whether open tracing should be enabled.
     *                      For example, it should return `true` in a DT preview environment,
     *                      but `false` in a DT page builder environment.
     */
    isOpenTraceEnabled() {
      return true;
    }

    /**
     * Adds DT-specific information to an object.
     * The default runtime implementation does nothing.
     *
     * @param {*} target - The object to annotate; initial use-case is something bound to a component,
     * which the DT can access.
     * @param {string} name - The name of the annotation.
     * @param {*} value - The value of the annotation.
     * @returns {boolean} - Should return `true` if the object was successfully annotated, `false` otherwise.
     */
    annotate(target, name, value) {
      return false;
    }

    /**
     * Returns the RequireJS path for the class in which the offline handler is defined. This gives
     * DT (Designer Toolkit) a chance to install its own offline handler for caching data purposes.
     *
     * @returns {Promise<String>} A promise that resolves to the RequireJS path for the offline handler class.
     */
    getOfflineHandlerUrl() {
      return Promise.resolve(null);
    }

    /**
     * Gives the Designer Toolkit (DT) a chance to inject an environmental context object.
     *
     * This method provides an environmental context object that can be accessed through the `$env` scope.
     * This context can be used to alter the behavior of a page in the designer, allowing for environment-specific
     * customizations or settings.
     *
     * @returns {Object} An object representing the environmental context.
     */
    getEnvironmentContext() {
      return {};
    }

    /**
     * Retrieves the access token and its expiration.
     *
     * The default runtime implementation does nothing as the access token needs to be acquired differently.
     * This method is intended to be overridden by subclasses to provide the actual implementation
     * for retrieval of an access token and its expiration.
     *
     * It returns a promise that resolves to an object containing the access token and its expiration details, such as:
     * {
     *   "access_token": "tokenvalue",
     *   "expires_in": 3600
     * }
     * or undefined if an access token is not available or if the method is not overridden.
     *
     * @returns {Promise<{access_token: string, expires_in: number} | undefined>}
     * A promise that resolves to either undefined or an object containing an access token and its expiration details.
     */
    getAccessToken() {
      return Promise.resolve(undefined);
    }
  }

  return RuntimeEnvironment;
});

